//Updated types with 6-stage tree system (0-5)


export interface UserPresence {
  userId: string;
  username: string;
  initials: string;
  position: { x: number; y: number };
  lastUpdate: Date;
  isActive: boolean;
  preferredColor?: string;
}

export interface Animal {
  id: string;
  name: string;
  shortDescription: string;
  longDescription: string;
  type: string;
  subtype: string;
  metadata: Record<string, any>; //NOTES: This is for the chatbot that may or may not exist. ToDo: Hook this up at some point
  imagePath: string;
  avatarPaths: {
    up: string;
    down: string;
    left: string;
    right: string;
  };
}

export interface Forest {
  id: string;
  name: string;
  slug: string;
  classCode: string;
  teacherId: string;
  dailyExerciseGroupId: string;
  gameDuration: number; //Denominated in days
  startDate: Date;
  endDate: Date;
  maxStudents?: number | null;
  dailyWaterLimit?: number | null; //Here, undefined means unlimited
  studentWaterLimit?: number | null; //Here, undefined means unlimited
  gameWaterLimit?: number | null; //Here, undefined means unlimited
  isActive: boolean;
  currentStudents?: number;
  isQuickGame?: boolean;
  timeMultiplier?: number; 
  isDraft?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface DailyExercise {
  id: string;
  groupId: string;
  day: number;
  mediaType: 0 | 1 | 2 | 3 | 4 | 5;
  //NOTE: Media Type Definitions:
  //0: Written instructions with file upload
  //1: Video (YouTube, Vimeo, etc.)
  //2: Written instructions only
  //3: Google Slides presentation
  //4: Google Doc
  //5: Generic URL
  mediaUrl: string;
  title: string;
  instructions?: string;
  createdAt: Date;
  updatedAt: Date;
}

//Track user exercise completion
export interface ExerciseCompletion {
  id: string;
  userId: string;
  forestId: string;
  exerciseId: string;
  exerciseDay: number;
  completedAt: Date;
  seedsEarned: number;
  createdAt: Date;
}

//User
export interface User {
  uid: string;
  username: string;
  email?: string;
  firstName: string;
  lastName: string;
  initials: string;
  classCode?: string;
  animalId: string;
  userType: 'student' | 'instructor';
  isActive: boolean;
  seeds: number;
  water: number;
  totalSeedsEarned: number;
  totalSeedsPlanted: number;
  totalWaterEarned: number;
  totalWaterUsedOnOthers: number;
  lastPosition: { x: number; y: number };
  showIntroduction: boolean;
  preferredColor?: string;
  completedExercises: number[];
  lastExerciseDate?: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

//Helper (modal props))
export interface DailyExerciseModalProps {
  isOpen: boolean;
  exercise: DailyExercise | null;
  onClose: () => void;
  onComplete: () => void;
}

//Media type display names
export const MEDIA_TYPE_NAMES = {
  0: 'Instructions with Upload',
  1: 'Video',
  2: 'Written Instructions', 
  3: 'Google Slides',
  4: 'Google Doc',
  5: 'Website/URL'
} as const;

//Tree system
export interface Tree {
  id: string;
  forestId: string;
  planterId: string;
  planterName: string;
  position: {
    x: number;
    y: number;
  };
  plantedAt: Date;
  maturityStage: 0 | 1 | 2 | 3 | 4 | 5; //
  //Stage 0: Seed (day 0-1)
  //Stage 1: Sprout (day 1-2) 
  //Stage 2: Seedling (day 2-3)
  //Stage 3: Sapling (day 3-4)
  //Stage 4: Young (day 4-5)
  //Stage 5: Mature (day 5+)
  waterReceived: number;
  estimatedMaturityDate: Date;
  chosenColor?: string; //User color
  createdAt: Date;
  updatedAt: Date;
}

export interface WaterAction {
  id: string;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  treeId: string;
  amount: number;
  timestamp: Date;
}

export interface ChatSession {
  id: string;
  userId: string;
  forestId: string;
  messages: ChatMessage[];
  waterEarned: number;
  isCompleted: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

//Game view tile types
export type TileType = 'plantable' | 'walkable' | 'border';

export interface GameTile {
  x: number;
  y: number;
  type: TileType;
  imagePath?: string;
  hasTree?: boolean;
  treeId?: string;
}

export interface GameGrid {
  width: number;
  height: number;
  tiles: GameTile[][];
}

//UI States
export interface IntroSlide {
  id: string;
  title: string;
  content: string;
  imagePath?: string;
}

export interface UIState {
  isLoading: boolean;
  error: string | null;
  showIntroModal: boolean;
  showChatbot: boolean;
  showTreeBubbles: boolean;
  showUserBubbles: boolean;
  currentSlide: number;
}

//Forms
export interface LoginForm {
  username: string;
  password: string;
}

export interface SignupForm {
  username: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  initials: string;
  email: string;
  classCode: string;
  selectedAnimalId: string;
}

//Teacher stats
export interface ForestStats {
  totalStudents: number;
  totalSeeds: number;
  totalMatureTrees: number;
  topSeedEarners: Array<{ userId: string; username: string; seeds: number }>;
  bottomSeedEarners: Array<{ userId: string; username: string; seeds: number }>;
  topWaterEarners: Array<{ userId: string; username: string; water: number }>;
  bottomWaterEarners: Array<{ userId: string; username: string; water: number }>;
}

export interface CreateForestForm {
  name: string;
  slug: string;
  classCode: string;
  dailyExerciseGroupId: string;
  gameDuration: number;
  dailyWaterLimit?: number;
}

export type GameAction = 
  | { type: 'plant_seed'; position: { x: number; y: number } }
  | { type: 'water_tree'; treeId: string }
  | { type: 'exit_menu' };

//Misc Info
export const AVAILABLE_COLORS = [
  { name: 'Red', hex: '#ef4444' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Green', hex: '#22c55e' },
  { name: 'Purple', hex: '#a855f7' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Pink', hex: '#ec4899' },
  { name: 'Yellow', hex: '#eab308' },
  { name: 'Cyan', hex: '#06b6d4' },
  { name: 'Magenta', hex: '#d946ef' },
  { name: 'Lime', hex: '#84cc16' }
] as const;

//Helper (fast mode))
export interface GameConfig {
  FAST_MODE: boolean;
  FAST_MODE_GROWTH_MULTIPLIER: number;
  FAST_MODE_GAME_DURATION_MINUTES: number;
}